import { supabase } from '../supabase';
import type { Tables, TablesInsert, TablesUpdate } from '../database.types';
import {
  PaginationParams,
  PaginatedResponse,
  calculatePaginationRange,
  buildPaginationInfo,
} from './pagination';
import { logger } from '../logger';
import { withRetry } from './withRetry';

export type Propuesta = Tables<'propuestas'>;
export type PropuestaInsert = TablesInsert<'propuestas'>;
export type PropuestaUpdate = TablesUpdate<'propuestas'>;

export interface PropuestaFilters {
  municipalityId?: string;
  categoryId?: string;
  status?: Propuesta['status'];
  dateFrom?: string;
  dateTo?: string;
  searchText?: string;
  reunionId?: string;
  prioridad?: 'Alta' | 'Media' | 'Baja' | 'null'; // Priority level or 'null' for IS NULL query
}

/**
 * Fetch propuestas with optional filters and joined data
 * Uses RPC function when searchText is provided to enable searching by municipality name
 */
export async function getPropuestas(filters?: PropuestaFilters): Promise<Propuesta[]> {
  // Use RPC function when searchText is provided to search across municipality name
  if (filters?.searchText) {
    const { data, error } = await withRetry(() =>
      supabase.rpc('search_propuestas', {
        p_search_text: filters.searchText,
        p_municipality_id: filters.municipalityId ?? null,
        p_status: filters.status ?? null,
        p_date_from: filters.dateFrom ?? null,
        p_date_to: filters.dateTo ?? null,
        p_reunion_id: filters.reunionId ?? null,
        p_prioridad: filters.prioridad ?? null,
      } as any)
    );

    if (error) throw error;
    return (data || []) as Propuesta[];
  }

  // Standard query when no search text (more efficient for simple filters)
  let query = supabase
    .from('propuestas')
    .select('*')
    .order('prioridad_order', { ascending: true })
    .order('date', { ascending: false });

  if (filters?.municipalityId) {
    query = query.eq('municipality_id', filters.municipalityId);
  }

  if (filters?.categoryId) {
    query = query.eq('category_id', filters.categoryId);
  }

  if (filters?.status) {
    query = query.eq('status', filters.status);
  }

  if (filters?.dateFrom) {
    query = query.gte('date', filters.dateFrom);
  }

  if (filters?.dateTo) {
    query = query.lte('date', filters.dateTo);
  }

  if (filters?.reunionId) {
    query = query.eq('reunion_id', filters.reunionId);
  }

  if (filters?.prioridad !== undefined) {
    if (filters.prioridad === 'null') {
      query = query.is('prioridad', null);
    } else {
      query = query.eq('prioridad', filters.prioridad);
    }
  }

  const { data, error } = await withRetry(() => query);

  if (error) throw error;
  return data || [];
}

/**
 * Fetch propuestas with pagination support
 * SCALE-CRIT-001 Fix: Prevents unbounded queries that can timeout at scale
 */
export async function getPropuestasPaginated(
  filters?: PropuestaFilters,
  pagination?: PaginationParams
): Promise<PaginatedResponse<Propuesta>> {
  const { from, to, page, pageSize } = calculatePaginationRange(pagination);

  // For RPC searches with pagination
  if (filters?.searchText) {
    const { data: allData, error: countError } = await withRetry(() =>
      supabase.rpc('search_propuestas', {
        p_search_text: filters.searchText,
        p_municipality_id: filters.municipalityId ?? null,
        p_status: filters.status ?? null,
        p_date_from: filters.dateFrom ?? null,
        p_date_to: filters.dateTo ?? null,
        p_reunion_id: filters.reunionId ?? null,
        p_prioridad: filters.prioridad ?? null,
      } as any)
    );

    if (countError) throw countError;

    const totalCount = (allData || []).length;
    const paginatedData = ((allData || []) as Propuesta[]).slice(from, to + 1);

    return {
      data: paginatedData,
      pagination: buildPaginationInfo(totalCount, page, pageSize),
    };
  }

  // Build count query
  let countQuery = supabase
    .from('propuestas')
    .select('*', { count: 'exact', head: true });

  if (filters?.municipalityId) {
    countQuery = countQuery.eq('municipality_id', filters.municipalityId);
  }
  if (filters?.categoryId) {
    countQuery = countQuery.eq('category_id', filters.categoryId);
  }
  if (filters?.status) {
    countQuery = countQuery.eq('status', filters.status);
  }
  if (filters?.dateFrom) {
    countQuery = countQuery.gte('date', filters.dateFrom);
  }
  if (filters?.dateTo) {
    countQuery = countQuery.lte('date', filters.dateTo);
  }
  if (filters?.reunionId) {
    countQuery = countQuery.eq('reunion_id', filters.reunionId);
  }
  if (filters?.prioridad !== undefined) {
    if (filters.prioridad === 'null') {
      countQuery = countQuery.is('prioridad', null);
    } else {
      countQuery = countQuery.eq('prioridad', filters.prioridad);
    }
  }

  const { count: totalCount, error: countError } = await withRetry(() => countQuery);
  if (countError) throw countError;

  // Build paginated query
  let query = supabase
    .from('propuestas')
    .select('*')
    .order('prioridad_order', { ascending: true })
    .order('date', { ascending: false })
    .range(from, to);

  if (filters?.municipalityId) {
    query = query.eq('municipality_id', filters.municipalityId);
  }
  if (filters?.categoryId) {
    query = query.eq('category_id', filters.categoryId);
  }
  if (filters?.status) {
    query = query.eq('status', filters.status);
  }
  if (filters?.dateFrom) {
    query = query.gte('date', filters.dateFrom);
  }
  if (filters?.dateTo) {
    query = query.lte('date', filters.dateTo);
  }
  if (filters?.reunionId) {
    query = query.eq('reunion_id', filters.reunionId);
  }
  if (filters?.prioridad !== undefined) {
    if (filters.prioridad === 'null') {
      query = query.is('prioridad', null);
    } else {
      query = query.eq('prioridad', filters.prioridad);
    }
  }

  const { data, error } = await withRetry(() => query);
  if (error) throw error;

  return {
    data: data || [],
    pagination: buildPaginationInfo(totalCount || 0, page, pageSize),
  };
}

/**
 * Get a single propuesta by ID
 */
export async function getPropuestaById(id: string): Promise<Propuesta | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('propuestas')
      .select('*')
      .eq('id', id)
      .single()
  );

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }
  return data;
}

/**
 * Get all propuestas linked to a specific reunion
 */
export async function getPropuestasByReunionId(reunionId: string): Promise<Propuesta[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('propuestas')
      .select('*')
      .eq('reunion_id', reunionId)
      .order('prioridad_order', { ascending: true })
      .order('date', { ascending: false })
  );

  if (error) throw error;
  return data || [];
}

/**
 * Create a new propuesta (folio auto-generated by database trigger)
 * NOTE: organization_id MUST be included in the propuesta object
 */
export async function createPropuesta(propuesta: Omit<PropuestaInsert, 'folio'>): Promise<Propuesta> {
  logger.log('[createPropuesta] Creating propuesta with data:', propuesta);

  const { data, error } = await withRetry(() =>
    supabase
      .from('propuestas')
      .insert({ ...propuesta, folio: '' }) // Trigger will generate folio
      .select()
      .single()
  );

  if (error) {
    logger.error('[createPropuesta] Error:', error);
    throw error;
  }

  logger.log('[createPropuesta] Success:', data);
  return data;
}

/**
 * Update an existing propuesta
 */
export async function updatePropuesta(id: string, updates: PropuestaUpdate): Promise<Propuesta> {
  logger.log('[updatePropuesta] Updating propuesta:', id, updates);

  const { data, error } = await withRetry(() =>
    supabase
      .from('propuestas')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
  );

  if (error) {
    logger.error('[updatePropuesta] Error:', error);
    throw error;
  }

  logger.log('[updatePropuesta] Success:', data);
  return data;
}

/**
 * Delete a propuesta
 */
export async function deletePropuesta(id: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase
      .from('propuestas')
      .delete()
      .eq('id', id)
  );

  if (error) throw error;
}
